/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: UserProfileFormFields.tsx
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Form fields component for User Profile.

 🔁 Last Updated: 2025-01-XX by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import React from 'react';
import { User, Upload, X } from 'lucide-react';
import { useI18n } from '../../../hooks/useI18n';
import { useDebugMode } from '../../../hooks/useDebugMode';
import { useDebugModeStore } from '../../../store/debugMode';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';
import { Toggle } from '../../../components/ui/Toggle';
import TimezoneSelect from '../../../components/ui/TimezoneSelect';
import LanguageSelect from '../../../components/ui/LanguageSelect';
import { FormBox } from '../../../components/ui/FormBox';
import MenuItemSelect from '../../../components/ui/MenuItemSelect';
import { useMenuStore } from '../../../store/menu';
import type { UserProfileData, ProfileFormErrors } from '../types';

interface UserProfileFormFieldsProps {
  formData: UserProfileData;
  errors: ProfileFormErrors;
  avatar: string | null;
  uploading: boolean;
  debugModePermission: boolean;
  handleInputChange: (field: keyof UserProfileData, value: string | boolean) => void;
  handleAvatarSelect: () => void;
  handleAvatarRemove: () => void;
}

export function UserProfileFormFields({
  formData,
  errors,
  avatar,
  uploading,
  debugModePermission,
  handleInputChange,
  handleAvatarSelect,
  handleAvatarRemove
}: UserProfileFormFieldsProps) {
  const { t } = useI18n();
  const { isActive: debugModeActive } = useDebugMode();
  const { getFinalItems } = useMenuStore();

  const finalMenuItems = React.useMemo(() => {
    const items = getFinalItems().map((item) => {
      let translated = t(`menu.sections.${item.title}`);
      if (translated === `menu.sections.${item.title}`) translated = item.title;
      return {
        id: item.id,
        name: translated,
        path: item.url || '',
      };
    });

    const dashboardLabel = t('menu.sections.dashboard');
    items.unshift({
       id: 0,
       name: dashboardLabel !== 'menu.sections.dashboard' ? dashboardLabel : 'Dashboard',
       path: '/dashboard'
    });

    return items;
  }, [getFinalItems, t]);

  return (
    <FormBox title="Profile Information" icon={<User />}>
      {/* 4-Column Grid Layout */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-center">
        {/* Row 1 */}
        <SimpleTooltip 
          content={t('profile.tooltips.username')}
          position="top"
          maxWidth="300px"
        >
          <label className="text-sm font-medium text-foreground">
            {t('profile.username')}
            {debugModeActive && (
              <span className="block text-xs text-muted-foreground font-mono">username</span>
            )}
          </label>
        </SimpleTooltip>
        <input
          type="text"
          value={formData.username}
          readOnly
          disabled
          className="px-3 py-2 text-sm border border-border rounded bg-muted text-muted-foreground cursor-not-allowed"
          placeholder={t('profile.enterUsername')}
        />
        <SimpleTooltip 
          content={t('profile.tooltips.email')}
          position="top"
          maxWidth="300px"
        >
          <label className="text-sm font-medium text-foreground">
            {t('profile.email')} *
            {debugModeActive && (
              <span className="block text-xs text-muted-foreground font-mono">email</span>
            )}
          </label>
        </SimpleTooltip>
        <input
          type="email"
          value={formData.email}
          onChange={(e) => handleInputChange('email', e.target.value)}
          className={`px-3 py-2 text-sm border rounded bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent ${
            errors.email ? 'border-red-500' : 'border-border'
          }`}
          placeholder={t('profile.enterEmail')}
        />

        {/* Row 2 */}
        <SimpleTooltip 
          content={t('profile.tooltips.fullName')}
          position="top"
          maxWidth="300px"
        >
          <label className="text-sm font-medium text-foreground">
            {t('profile.fullName')}
            {debugModeActive && (
              <span className="block text-xs text-muted-foreground font-mono">full_name</span>
            )}
          </label>
        </SimpleTooltip>
        <input
          type="text"
          value={formData.fullName}
          onChange={(e) => handleInputChange('fullName', e.target.value)}
          className="px-3 py-2 text-sm border border-border rounded bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent"
          placeholder={t('profile.enterFullName')}
        />
        <SimpleTooltip 
          content={t('profile.tooltips.department')}
          position="top"
          maxWidth="300px"
        >
          <label className="text-sm font-medium text-foreground">
            {t('profile.department')}
            {debugModeActive && (
              <span className="block text-xs text-muted-foreground font-mono">department</span>
            )}
          </label>
        </SimpleTooltip>
        <input
          type="text"
          value={formData.department}
          onChange={(e) => handleInputChange('department', e.target.value)}
          className="px-3 py-2 text-sm border border-border rounded bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent"
          placeholder={t('profile.enterDepartment')}
        />

        {/* Row 3 */}
        <SimpleTooltip 
          content={t('profile.tooltips.locale')}
          position="top"
          maxWidth="300px"
        >
          <label className="text-sm font-medium text-foreground">
            {t('profile.locale')}
            {debugModeActive && (
              <span className="block text-xs text-muted-foreground font-mono">locale</span>
            )}
          </label>
        </SimpleTooltip>
        <LanguageSelect
          value={formData.locale}
          onChange={(value) => handleInputChange('locale', value)}
          placeholder={t('profile.selectLanguage')}
        />
        <SimpleTooltip 
          content={t('profile.tooltips.timezone')}
          position="top"
          maxWidth="300px"
        >
          <label className="text-sm font-medium text-foreground">
            {t('profile.timezone')}
            {debugModeActive && (
              <span className="block text-xs text-muted-foreground font-mono">timezone</span>
            )}
          </label>
        </SimpleTooltip>
        <TimezoneSelect
          value={formData.timezone}
          onChange={(value) => handleInputChange('timezone', value)}
          placeholder={t('profile.selectTimezone')}
        />

        {/* Row 4 */}
        <SimpleTooltip 
          content={t('profile.tooltips.startupDialog')}
          position="top"
          maxWidth="300px"
        >
          <label className="text-sm font-medium text-foreground">
            {t('profile.startupDialog')}
            {debugModeActive && (
              <span className="block text-xs text-muted-foreground font-mono">startup_dialog</span>
            )}
          </label>
        </SimpleTooltip>
        <MenuItemSelect
          value={formData.startupDialog}
          onChange={(value) => handleInputChange('startupDialog', value)}
          options={finalMenuItems}
          placeholder={t('profile.selectStartupDialog')}
        />
        
        {/* Debug Mode Toggle - Only show if user has debug_mode permission */}
        {debugModePermission && (
          <>
            <SimpleTooltip 
              content={t('users.tooltips.debugModeActive')}
              position="top"
              maxWidth="300px"
            >
              <label className="text-sm font-medium text-foreground">
                {t('users.debugModeActive')}
                {debugModeActive && (
                  <span className="block text-xs text-muted-foreground font-mono">debug_mode_active</span>
                )}
              </label>
            </SimpleTooltip>
            <div className="flex items-center">
              <Toggle
                value={debugModeActive}
                onChange={(enabled) => {
                  handleInputChange('debugModeActive', enabled);
                  useDebugModeStore.getState().setDebugMode(enabled, debugModePermission);
                }}
                label=""
              />
            </div>
          </>
        )}
        
        {/* Avatar Row */}
        <SimpleTooltip 
          content={t('profile.tooltips.avatar')}
          position="top"
          maxWidth="300px"
        >
          <label className="text-sm font-medium text-foreground">
            {t('profile.avatar')}
            {debugModeActive && (
              <span className="block text-xs text-muted-foreground font-mono">avatar</span>
            )}
          </label>
        </SimpleTooltip>
        <div className="flex items-center gap-3">
          <div className="relative w-12 h-12 border-2 border-border rounded-lg bg-muted flex items-center justify-center">
            {avatar ? (
              <>
                <img 
                  src={avatar} 
                  alt="Avatar" 
                  className="w-full h-full object-cover rounded-lg"
                />
                {!uploading && (
                  <button
                    type="button"
                    onClick={handleAvatarRemove}
                    className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                    title="Remove avatar"
                  >
                    <X className="w-2.5 h-2.5 text-white" />
                  </button>
                )}
              </>
            ) : (
              <User className="w-6 h-6 text-muted-foreground" />
            )}
            {uploading && (
              <div className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={handleAvatarSelect}
            disabled={uploading}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-primary hover:text-primary/90 hover:bg-primary/10 rounded-md transition-colors border border-primary/20 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Upload className="w-3 h-3 text-primary" />
            {uploading ? t('profile.uploading') : (avatar ? t('profile.changeImage') : t('profile.selectImage'))}
          </button>
        </div>
        <div></div>
        <div></div>
      </div>
    </FormBox>
  );
}

